if (!customElements.get('build-box')) { 
  customElements.define('build-box', class BuildBox extends HTMLElement {
    constructor() {
      super();
      const rawData = this.querySelector('.box-products-data').textContent;
      this.families = JSON.parse(rawData);

      this.state = {
        selectedFamily: null,
        packSize: null,
        sellingPlanId: null,
        selectedFlavors: []
      };

      // Containers & Elements
      this.familyContainer = this.querySelector('[data-family-container]');
      this.stepSize = this.querySelector('[data-step-size]');
      this.sizeContainer = this.querySelector('[data-pack-size-container]');
      this.stepFrequency = this.querySelector('[data-step-frequency]');
      this.freqContainer = this.querySelector('[data-frequency-container]');
      this.stepFlavors = this.querySelector('[data-step-flavors]');
      this.flavorsContainer = this.querySelector('[data-flavors-container]');
      
      this.totalPriceEl = this.querySelector('[data-ui-total-price]');
      this.comparePriceEl = this.querySelector('[data-ui-compare-price]');
      this.submitBtn = this.querySelector('[data-build-box-btn]');
      this.currencyCode = this.getAttribute('data-currency-code') || 'EUR';
      
      const serverZeroPrice = this.totalPriceEl.innerText;
      this.decimalSeparator = serverZeroPrice.includes(',') ? ',' : '.';
    }

    connectedCallback() {
      this.renderFamilies();
      this.submitBtn.addEventListener('click', () => this.handleAddToCart());
    }

    // Step 1: Render Product Families
    renderFamilies() {
      this.familyContainer.innerHTML = '';
      if (!this.families || this.families.length === 0) return;

      this.families.forEach((family) => {
        const btn = document.createElement('button');
        btn.className = 'bb-option-btn family-btn';
        btn.innerText = family.title;

        btn.addEventListener('click', (e) => this.handleFamilyChange(e, family));
        this.familyContainer.appendChild(btn);
      });

      // Auto-select first family
      const firstBtn = this.familyContainer.querySelector('.family-btn');
      if (firstBtn) firstBtn.click();
    }

    handleFamilyChange(e, family) {
      this.querySelectorAll('.family-btn').forEach(b => b.classList.remove('active'));
      e.target.classList.add('active');

      this.state.selectedFamily = family;
      this.state.packSize = null;
      this.state.sellingPlanId = null;
      this.state.selectedFlavors = [];

      this.updateTotal();

      this.renderPackSizes();
      this.updateStepNumbers();
    }

    // Step 2: Render Pack Sizes / Options (Only variants WITH subscriptions)
    renderPackSizes() {
      this.sizeContainer.innerHTML = '';
      let uniqueSizes = new Set();

      this.state.selectedFamily.products.forEach(p => {
        p.variants.forEach(v => {
          if (v.selling_plans && v.selling_plans.length > 0) {
            uniqueSizes.add(v.title);
          }
        });
      });

      let sizes = Array.from(uniqueSizes);

      if (sizes.length === 0) {
        this.stepSize.style.display = 'none';
        this.stepFrequency.style.display = 'none';
        this.stepFlavors.style.display = 'block';
        this.flavorsContainer.innerHTML = '<span style="color: #ff7a50; font-size: 14px;">No subscription plans available for this category.</span>';
        this.updateTotal();
        this.updateStepNumbers();
        return;
      }

      if (sizes.length <= 1 && sizes[0] === 'Default Title') {
        this.stepSize.style.display = 'none';
        this.state.packSize = sizes[0];
        this.renderFrequencies();
        this.renderFlavors();
        this.updateTotal();
        return;
      }

      this.stepSize.style.display = 'block';

      sizes.sort((a, b) => {
        let numA = parseInt(a.match(/\d+/)?.[0] || 0);
        let numB = parseInt(b.match(/\d+/)?.[0] || 0);
        return numA - numB;
      });

      sizes.forEach(sizeTitle => {
        let isCompletelyOutOfStock = true;
        this.state.selectedFamily.products.forEach(p => {
          let v = p.variants.find(va => va.title === sizeTitle);
          if (v && v.available && v.selling_plans && v.selling_plans.length > 0) {
            isCompletelyOutOfStock = false;
          }
        });

        const btn = document.createElement('button');
        btn.className = 'bb-option-btn pack-size-btn';
        btn.innerText = sizeTitle.toLowerCase().includes('pack') ? sizeTitle : `${sizeTitle} pack`;

        if (isCompletelyOutOfStock) {
          btn.disabled = true;
          btn.innerText += ' (Sold Out)';
        } else {
          btn.addEventListener('click', (e) => this.handlePackSizeChange(e, sizeTitle));
        }

        this.sizeContainer.appendChild(btn);
      });

      const firstAvailable = this.sizeContainer.querySelector('.pack-size-btn:not(:disabled)');
      if (firstAvailable) firstAvailable.click();
    }

    handlePackSizeChange(e, sizeTitle) {
      this.querySelectorAll('.pack-size-btn').forEach(b => b.classList.remove('active'));
      e.target.classList.add('active');

      this.state.packSize = sizeTitle;
      this.state.selectedFlavors = [];

      this.renderFrequencies();
      this.renderFlavors();
      this.updateTotal();
    }

    // Step 3: Render Frequency / Selling Plans
    renderFrequencies() {
      this.freqContainer.innerHTML = '';
      this.state.sellingPlanId = null;

      let targetVariant = null;
      for (let prod of this.state.selectedFamily.products) {
        const v = prod.variants.find(va => va.title === this.state.packSize);
        if (v && v.selling_plans && v.selling_plans.length > 0) {
          targetVariant = v;
          break;
        }
      }

      if (!targetVariant || targetVariant.selling_plans.length === 0) {
        this.stepFrequency.style.display = 'none';
        this.updateStepNumbers();
        return;
      }

      this.stepFrequency.style.display = 'block';

      targetVariant.selling_plans.forEach((plan, index) => {
        const btn = document.createElement('button');
        if (index === 0) {
          btn.className = 'bb-option-btn freq-btn active';
          this.state.sellingPlanId = plan.id;
        } else {
          btn.className = 'bb-option-btn freq-btn';
        }

        btn.innerText = plan.name.replace('Delivery ', '');

        btn.addEventListener('click', (e) => {
          this.querySelectorAll('.freq-btn').forEach(b => b.classList.remove('active'));
          e.target.classList.add('active');
          this.state.sellingPlanId = plan.id;
          this.updateTotal();
        });

        this.freqContainer.appendChild(btn);
      });

      this.updateStepNumbers();
    }

    // Step 4: Render Flavors (Only products with valid subscription variants)
    renderFlavors() {
      this.flavorsContainer.innerHTML = '';
      this.stepFlavors.style.display = 'block';

      this.state.selectedFamily.products.forEach(product => {
        const variant = product.variants.find(v => v.title === this.state.packSize);
        
        if (!variant || !variant.selling_plans || variant.selling_plans.length === 0) return;

        const isAvailable = variant.available;
        const isActive = this.state.selectedFlavors.includes(product.id);

        const btn = document.createElement('button');
        btn.className = `bb-option-btn flavor-btn ${isActive ? 'active' : ''}`;
        btn.innerText = product.title;

        if (!isAvailable) {
          btn.disabled = true;
          btn.innerText += ' (Out of stock)';
        } else {
          btn.addEventListener('click', () => this.toggleFlavor(product.id));
        }

        this.flavorsContainer.appendChild(btn);
      });

      this.updateStepNumbers();
    }

    toggleFlavor(productId) {
      if (this.state.selectedFlavors.includes(productId)) {
        this.state.selectedFlavors = this.state.selectedFlavors.filter(id => id !== productId);
      } else {
        this.state.selectedFlavors.push(productId);
      }
      this.renderFlavors();
      this.updateTotal();
    }

    // Update Step Numbering dynamically (1, 2, 3...)
    updateStepNumbers() {
      const visibleSteps = this.querySelectorAll('.bb-step:not([style*="display: none"])');
      visibleSteps.forEach((step, index) => {
        const numEl = step.querySelector('[data-step-index]');
        if (numEl) numEl.innerText = index + 1;
      });
    }

    formatMoney(cents) {
      const locale = document.documentElement.lang || 'en-US';
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currencyCode,
      });

      const parts = formatter.formatToParts(cents / 100);

      return parts.map(part => {
        if (part.type === 'decimal') {
          return this.decimalSeparator;
        }
        return part.value;
      }).join('');
    }

    updateTotal() {
      let totalPrice = 0;
      let originalPrice = 0;

      this.state.selectedFlavors.forEach(prodId => {
        const product = this.state.selectedFamily.products.find(p => p.id === prodId);
        const variant = product.variants.find(v => v.title === this.state.packSize);

        if (variant) {
          originalPrice += variant.price;

          if (this.state.sellingPlanId) {
            const plan = variant.selling_plans.find(p => p.id === this.state.sellingPlanId);
            totalPrice += plan ? plan.price : variant.price;
          } else {
            totalPrice += variant.price;
          }
        }
      });

      this.totalPriceEl.innerText = this.formatMoney(totalPrice);

      if (originalPrice > totalPrice) {
        this.comparePriceEl.innerText = this.formatMoney(originalPrice);
        this.comparePriceEl.style.display = 'inline-block';
      } else {
        this.comparePriceEl.style.display = 'none';
      }

      if (this.state.selectedFamily && this.state.selectedFlavors.length > 0 && this.state.sellingPlanId) {
        this.submitBtn.disabled = false;
      } else {
        this.submitBtn.disabled = true;
      }
    }

    async handleAddToCart() {
      this.submitBtn.innerText = 'Adding...';
      this.submitBtn.disabled = true;

      let items = [];

      this.state.selectedFlavors.forEach(prodId => {
        const product = this.state.selectedFamily.products.find(p => p.id === prodId);
        const variant = product.variants.find(v => v.title === this.state.packSize);

        if (variant && this.state.sellingPlanId) {
          items.push({
            id: variant.id,
            quantity: 1,
            selling_plan: this.state.sellingPlanId
          });
        }
      });

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });

        if (response.ok) {
          window.location.href = '/cart';
        } else {
          const error = await response.json();
          alert("Error: " + error.description);
          this.resetSubmitButton();
        }
      } catch (err) {
        console.error(err);
        alert("Something went wrong!");
        this.resetSubmitButton();
      }
    }

    resetSubmitButton() {
      this.submitBtn.innerText = 'build my box →';
      this.submitBtn.disabled = false;
    }
  });
}